<script setup lang="ts">
import ButtonLink from "@/components/partials/ButtonLink.vue";
import { computed } from "vue";

import { Directory } from "@/types/documents/Directory";

const emit = defineEmits(["setCurrentDirectory"]);

const { directory } = defineProps<{
  directory: Directory;
}>();

// Computed property to get the correct document count to display
const displayCount = computed(() => {
  // If we have a filtered count (when filters are applied), use that
  if (directory.filtered_documents_count !== undefined) {
    return directory.filtered_documents_count;
  }
  // Otherwise use the total count
  return directory.documents_count;
});

const setDirectory = () => {
  emit("setCurrentDirectory", directory.id);
};
</script>

<template v-if="directory">
  <div class="modern-directory-card" @click="setDirectory">
    <!-- Directory icon -->
    <div class="directory-header">
      <div class="directory-icon-container">
        <div class="directory-icon">
          <v-icon size="28" color="white">mdi-folder</v-icon>
        </div>
      </div>
      <div class="directory-count">
        <span class="count-number">{{ displayCount }}</span>
        <span class="count-label">{{ displayCount === 1 ? $t('documents.document') : $t('common.documents') }}</span>
      </div>
    </div>

    <!-- Directory content -->
    <div class="directory-content">
      <h3 class="directory-title">{{ directory.name }}</h3>
      <p class="directory-description">
        {{ $t('documents.folder_contains_documents', { count: displayCount }) }}
      </p>
    </div>

    <!-- Directory actions -->
    <div class="directory-actions" @click.stop>
      <v-btn
        variant="text"
        size="small"
        class="action-button"
        @click="setDirectory"
      >
        <v-icon size="16">mdi-arrow-right</v-icon>
        {{ $t("common.open") }}
      </v-btn>
    </div>
  </div>
</template>

<style scoped lang="scss">
.modern-directory-card {
  position: relative;
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid #F1F5F9;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100%;
  display: flex;
  flex-direction: column;
  animation: slideInUp 0.6s ease-out both;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    border-color: #E2E8F0;

    .action-button {
      opacity: 1;
      background: rgba(59, 130, 246, 0.08) !important;
      color: #3B82F6 !important;
    }
  }

  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 12px;

    &:hover {
      transform: none;
    }
  }
}

.directory-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.directory-icon-container {
  .directory-icon {
    width: 56px;
    height: 56px;
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(5, 150, 105, 0.25);

    .v-icon {
      color: white !important;
    }
  }
}

.directory-count {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
}

.count-number {
  font-family: 'Archivo', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: #1E293B;
  line-height: 1;
}

.count-label {
  font-size: 12px;
  color: #64748B;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 2px;
}

.directory-content {
  flex: 1;
  margin-bottom: 16px;
}

.directory-title {
  font-family: 'Archivo', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #1E293B;
  margin: 0 0 8px 0;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.directory-description {
  font-size: 14px;
  color: #64748B;
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.directory-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-top: 12px;
  border-top: 1px solid #F1F5F9;
}

.action-button {
  opacity: 0.7;
  transition: all 0.2s ease;
  font-family: 'Archivo', sans-serif;
  font-weight: 600;
  text-transform: none;
  font-size: 13px;

  @media (max-width: 768px) {
    opacity: 1;
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .directory-title {
    font-size: 16px;
  }

  .directory-description {
    font-size: 13px;
  }

  .directory-icon-container .directory-icon {
    width: 48px;
    height: 48px;

    .v-icon {
      font-size: 24px !important;
    }
  }

  .count-number {
    font-size: 20px;
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
